import { useMemo, useState, type ReactNode } from "react";
import { AuthProvider, useAuth } from "./providers/AuthProvider";
import { TelegramProvider, useTelegram } from "./providers/TelegramProvider";
import { Layout, type NavigationItem } from "./components/Layout";
import { LoadingSpinner } from "./components/LoadingSpinner";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { useHomeScreenInstall } from "./hooks/usePwaInstall";
import { TeacherHome } from "./pages/TeacherHome";
import { StudentHome } from "./pages/StudentHome";
import { AttendanceMatrix } from "./pages/AttendanceMatrix";
import { MakeupsQueue } from "./pages/MakeupsQueue";
import { Schedule } from "./pages/Schedule";
import { AvailableSlots } from "./pages/AvailableSlots";

type TeacherTab = "teacher-home" | "attendance-matrix" | "makeups-queue";
type StudentTab = "available-slots" | "schedule" | "student-home";

const teacherNav: NavigationItem[] = [
  { key: "teacher-home", label: "Главная", icon: "⌂" },
  { key: "attendance-matrix", label: "Посещаемость", icon: "✓" },
  { key: "makeups-queue", label: "Отработки", icon: "↺" },
];

const studentNav: NavigationItem[] = [
  { key: "available-slots", label: "Отработка", icon: "↺" },
  { key: "schedule", label: "Расписание", icon: "◷" },
  { key: "student-home", label: "Группа", icon: "●●" },
];

const teacherTitles: Record<TeacherTab, string> = {
  "teacher-home": "Главная",
  "attendance-matrix": "Посещаемость",
  "makeups-queue": "Отработки",
};

const studentTitles: Record<StudentTab, string> = {
  "available-slots": "Отработка",
  schedule: "Расписание",
  "student-home": "Группа",
};

const DEFAULT_ROLE_SWITCH_TELEGRAM_IDS = ["470227422", "1390927139"];

const resolveRoleSwitchTelegramIds = (): Set<string> => {
  const raw = import.meta.env.VITE_TMA_ROLE_SWITCH_TELEGRAM_IDS;
  const parsed = String(raw ?? "")
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);

  return new Set(parsed.length > 0 ? parsed : DEFAULT_ROLE_SWITCH_TELEGRAM_IDS);
};

const resolveMakeupCancelRequestId = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  const directAction = params.get("makeupAction")?.trim().toLowerCase();
  const directId = params.get("makeupRequestId")?.trim();

  if (directAction === "cancel" && directId) {
    return directId;
  }

  const rawStartParam =
    params.get("tgWebAppStartParam") ||
    params.get("startapp") ||
    params.get("start");
  const startParam = rawStartParam?.trim();
  if (!startParam) {
    return null;
  }

  const match = startParam.match(/^cancel_makeup_(.+)$/);
  return match?.[1]?.trim() || null;
};

const RuntimeErrorFallback = () => (
  <main className="app-shell">
    <section className="panel alert">
      Произошла ошибка приложения. Перезагрузите страницу.
    </section>
  </main>
);

const AuthenticatedShell = () => {
  const telegram = useTelegram();
  const auth = useAuth();
  const homeScreen = useHomeScreenInstall();
  const [teacherTab, setTeacherTab] = useState<TeacherTab>("teacher-home");
  const [studentTab, setStudentTab] = useState<StudentTab>("available-slots");
  const [preferredMakeupLessonNo, setPreferredMakeupLessonNo] = useState<
    number | null
  >(null);
  const staffMakeupCancelRequestId = useMemo(
    () => resolveMakeupCancelRequestId(),
    [],
  );

  const installButton = homeScreen.showHeaderInstallButton
    ? { onClick: homeScreen.addToHomeScreen }
    : undefined;

  if (!telegram.isReady || auth.status === "loading") {
    return (
      <Layout
        title="Master Slova"
        subtitle="Загрузка"
        installButton={installButton}
      >
        <LoadingSpinner label="Пожалуйста, подождите..." />
      </Layout>
    );
  }

  if (auth.status !== "authenticated" || !auth.session) {
    const subtitle =
      auth.status === "error" ? "Ошибка авторизации" : "Требуется вход";

    return (
      <Layout
        title="Master Slova"
        subtitle={subtitle}
        installButton={installButton}
      >
        <div className="alert">
          {auth.status === "error" ? "Ошибка авторизации: " : ""}
          {auth.reason ?? "Не удалось авторизоваться в текущем режиме."}
        </div>
      </Layout>
    );
  }

  const { user } = auth.session;
  const allowedRoleSwitchTelegramIds = resolveRoleSwitchTelegramIds();
  const normalizedTelegramId = user.telegramId.trim();
  const canSwitchRoleInHeader =
    auth.mode === "mock" ||
    allowedRoleSwitchTelegramIds.has(normalizedTelegramId);
  const effectiveRole =
    user.role === "teacher" ||
    (staffMakeupCancelRequestId &&
      allowedRoleSwitchTelegramIds.has(normalizedTelegramId))
      ? "teacher"
      : user.role;
  const effectiveTeacherTab = staffMakeupCancelRequestId
    ? "makeups-queue"
    : teacherTab;

  const handleRoleSwitch = (nextRole: "student" | "teacher") => {
    const params = new URLSearchParams(window.location.search);
    const currentMockTelegramId = params.get("mockTelegramId")?.trim();
    const effectiveMockTelegramId = currentMockTelegramId || user.telegramId;

    if (effectiveMockTelegramId) {
      params.set("mockTelegramId", effectiveMockTelegramId);
    }

    params.set("mockRole", nextRole);

    console.info(
      `[TMA] Header role switch: nextRole=${nextRole}, mockTelegramId=${effectiveMockTelegramId || "-"}`,
    );

    window.location.search = `?${params.toString()}`;
  };

  if (effectiveRole === "teacher") {
    let teacherContent: ReactNode;
    switch (effectiveTeacherTab) {
      case "attendance-matrix":
        teacherContent = <AttendanceMatrix />;
        break;
      case "makeups-queue":
        teacherContent = (
          <MakeupsQueue
            initialCancelMakeupRequestId={staffMakeupCancelRequestId}
          />
        );
        break;
      case "teacher-home":
      default:
        teacherContent = <TeacherHome />;
        break;
    }

    return (
      <Layout
        title={teacherTitles[effectiveTeacherTab]}
        subtitle="Кабинет преподавателя"
        roleSwitcher={
          canSwitchRoleInHeader
            ? {
                value: effectiveRole,
                onChange: handleRoleSwitch,
              }
            : undefined
        }
        navItems={teacherNav}
        activeNavKey={effectiveTeacherTab}
        onNavigate={(key) => setTeacherTab(key as TeacherTab)}
        installButton={installButton}
      >
        {teacherContent}
      </Layout>
    );
  }

  let studentContent: ReactNode;
  switch (studentTab) {
    case "schedule":
      studentContent = (
        <Schedule
          onNavigateToMakeup={(lessonNo) => {
            setPreferredMakeupLessonNo(lessonNo);
            setStudentTab("available-slots");
          }}
        />
      );
      break;
    case "available-slots":
      studentContent = (
        <AvailableSlots preferredLessonNo={preferredMakeupLessonNo} />
      );
      break;
    case "student-home":
    default:
      studentContent = <StudentHome user={user} />;
      break;
  }

  return (
    <Layout
      title={studentTitles[studentTab]}
      subtitle={
        user.flowNo?.trim() ? `Поток ${user.flowNo.trim()}` : "Кабинет студента"
      }
      roleSwitcher={
        canSwitchRoleInHeader
          ? {
              value: user.role,
              onChange: handleRoleSwitch,
            }
          : undefined
      }
      navItems={studentNav}
      activeNavKey={studentTab}
      onNavigate={(key) => setStudentTab(key as StudentTab)}
      installButton={installButton}
    >
      {studentContent}
    </Layout>
  );
};

const App = () => (
  <TelegramProvider>
    <AuthProvider>
      <ErrorBoundary fallback={<RuntimeErrorFallback />}>
        <AuthenticatedShell />
      </ErrorBoundary>
    </AuthProvider>
  </TelegramProvider>
);

export default App;
